import { memo, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
// @ts-expect-error not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import PropTypes from 'prop-types';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import { isFailed, isStarted } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isResultSuccessful, getDuration, getHasRecording, getIsCRMObjectDeleted, getIsRecordingDeleted, getResult, getIsCallInProgress, getCRMObjectId, getHasTranscript, getTranscriptReady, getCallSummary, getCallSummaryExecutionId, getSuggestedNextBestActions } from '../call-message/operators/callMessageGetters';
import { getMessageDirection } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import AssociationsSelectWrapper from '../associations/components/AssociationsSelectWrapper';
import CallMetadataError from './CallMetadataError';
import CallRecording from './CallRecording';
import ReviewCallRecord from './ReviewCallRecord';
import MutableCallNotes from './MutableCallNotes';
import MutableCallDisposition from '../call-disposition/components/MutableCallDisposition';
import MutableCallType from '../call-type/components/MutableCallType';
import CallDuration from './CallDuration';
import TimelineCallMetadataSkeleton from './TimelineCallMetadataSkeleton';
import { withCallMessage } from '../hoc/withCallMessage';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import { DISTANCE_MEASUREMENT_SMALL } from 'HubStyleTokens/sizes';
import CallSummary from 'call-summary/components/CallSummary';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { CALLING_NEXT_BEST_ACTIONS } from 'calling-internal-common/constants/CallingGates';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDividerLine = styled.div.withConfig({
  displayName: "TimelineCallMetadataWithDetails__StyledDividerLine",
  componentId: "ic5fh5-0"
})(["border-bottom:1px solid ", ";"], GREAT_WHITE);
const StyledCallMetadataSection = styled.section.withConfig({
  displayName: "TimelineCallMetadataWithDetails__StyledCallMetadataSection",
  componentId: "ic5fh5-1"
})(["display:flex;flex-direction:row;"]);
export function TimelineCallMetadataWithDetails({
  asyncCallMessage,
  handlePostCallPolling,
  handleEngagementDataUpdated,
  maxPollAttemptsReached,
  pollUntilCallCompleted,
  resetCallMessage,
  onDeleteRecording,
  timestamp,
  message,
  ungatedForNextBestActions
}) {
  const alertStore = FloatingAlertStore.newFloatingAlertStore();
  const callMessage = getData(asyncCallMessage);
  const callDirection = getMessageDirection(message);
  const callDuration = getResult(getDuration(callMessage));
  const callInProgress = getIsCallInProgress(callMessage);
  const hasRecording = getResult(getHasRecording(callMessage));
  const isRecordingDeleted = getResult(getIsRecordingDeleted(callMessage));
  const renderCallRecording = hasRecording && !isRecordingDeleted;
  useEffect(() => {
    if (callInProgress) {
      pollUntilCallCompleted();
    }
  }, [callInProgress, pollUntilCallCompleted]);
  useEffect(() => {
    if (!callInProgress && !isResultSuccessful(callMessage)) {
      handlePostCallPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const renderCallSummary = useMemo(() => {
    const callSummary = getResult(getCallSummary(callMessage));
    const callSummaryExecutionId = getResult(getCallSummaryExecutionId(callMessage));
    const initialNextBestActions = getResult(getSuggestedNextBestActions(callMessage));
    const callId = getCRMObjectId(callMessage);
    const engagementId = getCRMObjectId(callMessage);
    const hasTranscript = getResult(getHasTranscript(callMessage));
    const transcriptReady = getResult(getTranscriptReady(callMessage));
    const isTranscriptScheduled = hasTranscript && !transcriptReady;
    const gates = ungatedForNextBestActions ? [CALLING_NEXT_BEST_ACTIONS] : [];
    return /*#__PURE__*/_jsx("section", {
      children: /*#__PURE__*/_jsx(CallSummary, {
        callId: callId,
        canEdit: true,
        engagementId: engagementId,
        handleEngagementDataUpdated: handleEngagementDataUpdated,
        initialCallSummary: callSummary,
        initialCallSummaryExecutionId: callSummaryExecutionId,
        isTranscriptEmpty: !hasTranscript,
        isTranscriptScheduled: isTranscriptScheduled,
        usageTracker: CommunicatorLogger,
        initialNextBestActions: initialNextBestActions,
        gates: gates,
        style: {
          borderBottom: `1px solid ${GREAT_WHITE}`,
          marginBottom: 0,
          paddingTop: DISTANCE_MEASUREMENT_SMALL,
          paddingBottom: DISTANCE_MEASUREMENT_SMALL
        }
      })
    });
  }, [callMessage, handleEngagementDataUpdated, ungatedForNextBestActions]);
  if (isStarted(asyncCallMessage)) {
    return /*#__PURE__*/_jsx(TimelineCallMetadataSkeleton, {});
  } else if (isFailed(asyncCallMessage)) {
    return /*#__PURE__*/_jsx(CallMetadataError, {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "conversations-calling-message.timeline-call-metadata.generic-error_jsx",
        options: {
          onClick: resetCallMessage
        },
        elements: {
          UILink
        }
      })
    });
  }
  if (!isResultSuccessful(callMessage)) {
    if (maxPollAttemptsReached) {
      return /*#__PURE__*/_jsx(UIAlert, {
        type: "danger",
        use: "inline",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "conversations-calling-message.timeline-call-metadata.generic-error_jsx",
          options: {
            onClick: resetCallMessage
          },
          elements: {
            UILink
          }
        })
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "conversations-calling-message.timeline-call-metadata.processing"
    });
  }
  if (getIsCRMObjectDeleted(callMessage)) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "timeline-call-metadata-with-details",
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "call-engagement-deleted",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "conversations-calling-message.timeline-call-metadata.engagement-deleted"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "timeline-call-metadata-with-details",
    children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {
      alertStore: alertStore,
      use: "contextual"
    }), /*#__PURE__*/_jsx(ReviewCallRecord, {
      callMessage: callMessage
    }), /*#__PURE__*/_jsx(MutableCallNotes, {
      callMessage: callMessage,
      handleEngagementDataUpdated: handleEngagementDataUpdated,
      message: message,
      resetCallMessage: resetCallMessage
    }), /*#__PURE__*/_jsx(StyledDividerLine, {}), renderCallSummary, /*#__PURE__*/_jsxs(StyledCallMetadataSection, {
      children: [/*#__PURE__*/_jsx(MutableCallDisposition, {
        callDirection: callDirection,
        callMessage: callMessage,
        handleEngagementDataUpdated: handleEngagementDataUpdated
      }), /*#__PURE__*/_jsx(MutableCallType, {
        callMessage: callMessage,
        handleEngagementDataUpdated: handleEngagementDataUpdated
      }), /*#__PURE__*/_jsx(CallDuration, {
        callInProgress: callInProgress,
        duration: callDuration
      })]
    }), !callInProgress && renderCallRecording && /*#__PURE__*/_jsx(CallRecording, {
      callMessage: callMessage,
      duration: callDuration,
      handlePostCallPolling: handlePostCallPolling,
      resetCallMessage: resetCallMessage,
      onDeleteRecording: onDeleteRecording,
      timestamp: timestamp
    }), /*#__PURE__*/_jsx(AssociationsSelectWrapper, {
      alertStore: alertStore,
      callMessage: callMessage,
      message: message
    })]
  });
}
TimelineCallMetadataWithDetails.propTypes = {
  callMetadata: RecordPropType('CallMetadata').isRequired,
  asyncCallMessage: RecordPropType('AsyncData').isRequired,
  ungatedForNextBestActions: PropTypes.bool,
  resetCallMessage: PropTypes.func.isRequired,
  onDeleteRecording: PropTypes.func.isRequired,
  timestamp: PropTypes.number.isRequired
};
export default withCallMessage( /*#__PURE__*/memo(TimelineCallMetadataWithDetails));